import React from "react";
import { TableCell, Box, Typography, Tooltip, IconButton, Chip } from "@mui/material";
import { Info as InfoIcon, Search as SearchIcon } from "@mui/icons-material";
import { googleShoppingUrl } from "../../../utils/googleSearch";
import type { RFQItem } from "../../../types/rfq";
import PriceSourceTooltip from './PriceSourceTooltip';
import ErrorBoundary from "../../shared/ErrorBoundary";

interface CostPriceCellProps {
  item: RFQItem;
  /** The row's current (possibly edited) name and model, for the search link. */
  itemName: string;
  modelNumber: string;
}

/** The item row's cost price, its price-source tooltip, and a Google Shopping link. */
const CostPriceCell: React.FC<CostPriceCellProps> = ({ item, itemName, modelNumber }) => {
  // Get appropriate icon for cost source
  const hasNoCostPrice = item.cost_price == null;
  // Only the research-pipeline shape carries cost_price_details; the tooltip
  // shows "no pricing data" for anything else, as it does for null.
  const priceSources = item.price_sources;
  const researchSources = priceSources && 'cost_price_details' in priceSources ? priceSources : null;
  const costPriceDetails = researchSources?.cost_price_details;
  // Plain stationery DBR does not normally supply (backend services/notOffered.js):
  // pre-filled at $0.00 on purpose, for the salesperson to validate or price.
  const isNotOffered = costPriceDetails?.source_type === 'not_offered';

  const getCostSourceIcon = () => {
    if (!costPriceDetails) return <InfoIcon color="disabled" sx={{ fontSize: 20 }} />;

    const confidence = costPriceDetails.confidence;
    switch (confidence) {
      case "high":
        return <InfoIcon color="success" sx={{ fontSize: 20 }} />;
      case "medium":
        return <InfoIcon color="warning" sx={{ fontSize: 20 }} />;
      case "low":
      case "very_low":
        return <InfoIcon color="error" sx={{ fontSize: 20 }} />;
      default:
        return <InfoIcon color="disabled" sx={{ fontSize: 20 }} />;
    }
  };

  return (
    <TableCell>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 64, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
          {item.cost_price != null ? `$${Number(item.cost_price).toFixed(2)}` : "-"}
        </Typography>
        {isNotOffered && (
          <Tooltip title={costPriceDetails?.explanation || "DBR does not normally supply this - validate or price it."}>
            <Chip label="Not offered" size="small" color="warning" variant="outlined" />
          </Tooltip>
        )}
        <ErrorBoundary
          fallback={
            <Tooltip title="Price source data unavailable">
              <InfoIcon sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
            </Tooltip>
          }
        >
          <Tooltip
            title={<PriceSourceTooltip priceSources={researchSources} />}
            arrow
            placement="right"
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: 'transparent',
                  p: 0,
                  maxWidth: 'none' // Allow tooltip to be as wide as needed
                }
              },
              arrow: {
                sx: {
                  color: 'background.paper'
                }
              }
            }}
            slotProps={{
              popper: {
                modifiers: [{
                  name: 'offset',
                  options: {
                    offset: [0, -8],
                  },
                }]
              }
            }}
          >
            <Box component="span" sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'help',
              ml: 0.5,
            }}>
              {getCostSourceIcon()}
            </Box>
          </Tooltip>
        </ErrorBoundary>
        {/* A real link, not window.open: no popup blocker, and ctrl/middle-click
            opens a background tab. Highlighted when DBR's data found no price. */}
        <Tooltip
          title={hasNoCostPrice
            ? "No price in DBR's records - search Google Shopping (SG)"
            : "Search Google Shopping (SG)"}
        >
          <IconButton
            size="small"
            component="a"
            href={googleShoppingUrl(itemName, modelNumber)}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Search Google Shopping for this item"
            color={hasNoCostPrice ? "primary" : "default"}
          >
            <SearchIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </TableCell>
  );
};

export default CostPriceCell;
